import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSuspectByIncidentRegistrationNumber } from "services/lealIntityService";
import { Suspect } from "types";

/**
 * Header chip naming the suspects of an incident: the first suspect's name
 * and "+N" for the rest; hovering (or focusing / tapping) lists them all.
 */
const fullName = (s: Suspect) =>
  [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ").trim();

const SuspectsChip: React.FC<{ incidentRegistrationNumber?: string }> = ({
  incidentRegistrationNumber,
}) => {
  const { t } = useTranslation();
  const [names, setNames] = useState<string[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!incidentRegistrationNumber) {
      setNames([]);
      return;
    }
    let current = true;
    fetchSuspectByIncidentRegistrationNumber(incidentRegistrationNumber)
      .then((data: any) => {
        const list: Suspect[] = Array.isArray(data) ? data : data?.results || [];
        if (current) setNames(list.map(fullName).filter(Boolean));
      })
      .catch(() => current && setNames([]));
    return () => {
      current = false;
    };
  }, [incidentRegistrationNumber]);

  const extra = (names?.length || 0) - 1;

  return (
    <div
      className="relative flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={() => setOpen((o) => !o)}
      tabIndex={names && names.length > 1 ? 0 : -1}
      aria-label={names?.join(", ")}
    >
      <span className="font-semibold text-red-600">{t("suspects")}:</span>
      <span className="text-gray-900 font-medium max-w-[14rem] truncate">
        {names === null ? "…" : names[0] || "-"}
      </span>
      {extra > 0 && (
        <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
          +{extra}
        </span>
      )}

      {open && names && names.length > 1 && (
        <div className="absolute right-0 top-full z-30 mt-2 w-72 max-w-[90vw] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("suspects")} ({names.length})
          </div>
          <ol className="max-h-64 overflow-y-auto py-1">
            {names.map((name, i) => (
              <li key={`${name}-${i}`} className="flex gap-2 px-4 py-1.5 text-sm text-gray-800">
                <span className="w-5 text-right text-gray-400">{i + 1}.</span>
                <span>{name}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default SuspectsChip;
